<script setup lang="ts">
import { computed, ref, toRef } from "vue";
import { useInfiniteQuery } from "@tanstack/vue-query";
import EditionHitsGrid from "@/components/editions/EditionHitsGrid.vue";
import LoadingIndicator from "@/components/general/LoadingIndicator.vue";
import { ErrorState, EmptyState } from "@/components/states";
import { useInfinityScroll } from "@/lib/infinityScroll";
import { getTitleEditionHitsSearch, EditionHitsInclude } from "@/repository";
import type { EditionHit } from "@/types/edition";

const props = defineProps<{ searchQuery: string }>();

const scrollComponent = ref<HTMLElement>();

const {
  data,
  isLoading,
  isError,
  refetch,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
} = useInfiniteQuery({
  queryKey: ["edition-search-tab", toRef(props, "searchQuery")],
  queryFn: ({ pageParam }) =>
    getTitleEditionHitsSearch({
      query: props.searchQuery,
      cursor: pageParam as string,
      include: [
        EditionHitsInclude.EndUserEditionData,
        EditionHitsInclude.EndUserSubscription,
      ],
    }),
  initialPageParam: "null",
  getNextPageParam: (lastPage) => lastPage.data.meta?.next_cursor,
  enabled: computed(() => props.searchQuery.length > 1),
});

const hits = computed<EditionHit[]>(
  () => data.value?.pages.flatMap((page) => page.data.data) ?? []
);

const isEmpty = computed(() => {
  // Only show empty state if query was enabled and executed successfully
  const isEnabled = props.searchQuery.length > 1;
  return isEnabled && data.value && hits.value.length === 0;
});

const fetchPage = () => {
  if (isFetchingNextPage.value) return;
  fetchNextPage();
};

useInfinityScroll({
  scrollComponent,
  fetchNextPage: fetchPage,
  scrollContainer: "#search-modal-container",
});
</script>

<template>
  <div class="p-4" ref="scrollComponent">
    <div v-if="isLoading || isError || isEmpty" class="flex-center min-h-40">
      <LoadingIndicator v-if="isLoading" class="text-icon-brand" />
      <ErrorState v-else-if="isError" :contentName="$t('common.editions')" @refetch="refetch" />
      <EmptyState
        v-else-if="isEmpty"
        :title="$t('search.no_results.title')"
        :description="$t('search.no_results.description')"
      />
    </div>

    <template v-else>
      <EditionHitsGrid :hits="hits" :query="props.searchQuery" />

      <div v-if="isFetchingNextPage" class="w-full flex-center py-4">
        <LoadingIndicator class="text-icon-brand" />
      </div>
      <div v-else-if="hasNextPage" class="w-full flex-center py-4">
        <button class="btn-primary" @click="fetchPage">
          {{ $t("common.load_more") }}
        </button>
      </div>
    </template>
  </div>
</template>

